import { useState, useEffect, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Button } from '../../../shared/components/ui/Button.js'
import { Input } from '../../../shared/components/ui/Input.js'
import { Modal } from '../../../shared/components/ui/Modal.js'
import { api } from './api.js'
import { ErrorBox } from './ErrorBox.js'
import {
  EMPTY_CONTA_FORM,
  EMPTY_EMPRESA_FORM,
  maskCNPJ,
  type ContaFormData,
  type ContaPlan,
  type EmpresaFormData,
} from './types.js'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
}

interface CreateContaResponse {
  conta: { id: string; nome: string; email: string }
  tempPassword: string
}

/**
 * Wizard 2 passos (porte fiel do super.old):
 *   1. Cadastra conta + user → API gera senha temporária
 *   2. (Opcional) Adiciona 1ª empresa vinculada — pode pular
 */
export function NovaContaModal({ open, onClose, onSaved }: Props) {
  const [step, setStep] = useState<1 | 2>(1)
  const [contaForm, setContaForm] = useState<ContaFormData>(EMPTY_CONTA_FORM)
  const [empresaForm, setEmpresaForm] = useState<EmpresaFormData>(EMPTY_EMPRESA_FORM)
  const [createdContaId, setCreatedContaId] = useState<string | null>(null)
  const [tempPassword, setTempPassword] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (open) {
      setStep(1)
      setContaForm(EMPTY_CONTA_FORM)
      setEmpresaForm(EMPTY_EMPRESA_FORM)
      setCreatedContaId(null)
      setTempPassword(null)
      setError('')
      setCopied(false)
    }
  }, [open])

  async function handleCriarConta(e: FormEvent) {
    e.preventDefault()
    if (!contaForm.nome.trim() || !contaForm.email.trim()) return
    setLoading(true)
    setError('')
    try {
      const res = await api.post<CreateContaResponse>('/api/v1/super/contas', {
        nome: contaForm.nome,
        email: contaForm.email,
        telefone: contaForm.telefone || null,
        plan: contaForm.plan,
        status: 'active',
      })
      setCreatedContaId(res.conta.id)
      setTempPassword(res.tempPassword)
      onSaved()
      setStep(2)
      toast.success('Conta criada', { description: `${res.conta.nome} · ${res.conta.email}` })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro inesperado'
      setError(msg)
      toast.error('Erro ao criar conta', { description: msg })
    } finally {
      setLoading(false)
    }
  }

  async function handleAdicionarEmpresa(e: FormEvent) {
    e.preventDefault()
    if (!empresaForm.nome.trim() || !createdContaId) return
    setLoading(true)
    setError('')
    try {
      await api.post(`/api/v1/super/contas/${createdContaId}/empresas`, {
        nome: empresaForm.nome,
        cnpj: empresaForm.cnpj || null,
        razao_social: empresaForm.razao_social || null,
      })
      onSaved()
      onClose()
      toast.success('Empresa adicionada', { description: empresaForm.nome })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro inesperado'
      setError(msg)
      toast.error('Erro ao adicionar empresa', { description: msg })
    } finally {
      setLoading(false)
    }
  }

  function handleCopiarSenha() {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={step === 1 ? 'Nova conta' : 'Adicionar empresa'}
      size="md"
    >
      {step === 1 ? (
        <form onSubmit={handleCriarConta} className="flex flex-col gap-4 mt-2">
          <Input
            label="Nome do responsável"
            placeholder="João Silva"
            value={contaForm.nome}
            onChange={(e) => setContaForm((f) => ({ ...f, nome: e.target.value }))}
            autoFocus
          />
          <Input
            label="E-mail"
            placeholder="joao@farmacia.com.br"
            type="email"
            value={contaForm.email}
            onChange={(e) => setContaForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Telefone"
            placeholder="(11) 99999-9999"
            value={contaForm.telefone}
            onChange={(e) => setContaForm((f) => ({ ...f, telefone: e.target.value }))}
            inputMode="tel"
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#374151]">Plano</label>
            <select
              value={contaForm.plan}
              onChange={(e) => setContaForm((f) => ({ ...f, plan: e.target.value as ContaPlan }))}
              className="h-10 rounded-lg border border-[#d1d5db] px-3 text-sm text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a4ee3] focus:border-transparent"
            >
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <p className="text-xs text-[#94a3b8]">
            A conta será criada com status <strong>Ativo</strong>. Uma senha temporária será gerada
            automaticamente.
          </p>
          {error && <ErrorBox message={error} />}
          <div className="flex justify-end gap-3 mt-2 pt-4 border-t border-[#f1f5f9]">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!contaForm.nome.trim() || !contaForm.email.trim()}
            >
              Criar conta →
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleAdicionarEmpresa} className="flex flex-col gap-4 mt-2">
          {/* Banner sucesso + senha */}
          <div className="flex flex-col gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" className="shrink-0">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <p className="text-sm text-green-700 font-medium">Conta criada com sucesso!</p>
            </div>
            {tempPassword && (
              <div>
                <p className="text-xs text-green-600 mb-1.5">Senha temporária (repasse ao cliente):</p>
                <div className="flex items-center gap-2 bg-white border border-green-200 rounded-lg px-3 py-2">
                  <code className="flex-1 text-sm font-mono text-[#0f172a] tracking-wider">
                    {tempPassword}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopiarSenha}
                    className="flex items-center gap-1.5 shrink-0 text-xs font-medium text-[#94a3b8] hover:text-[#0a4ee3] transition-colors"
                    title="Copiar senha"
                  >
                    {copied ? (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span className="text-green-600">Copiado!</span>
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copiar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#f1f5f9] pt-4">
            <p className="text-sm font-medium text-[#374151] mb-3">Adicionar primeira empresa</p>
            <div className="flex flex-col gap-3">
              <Input
                label="Nome da farmácia"
                placeholder="Farmácia Saúde Vital"
                value={empresaForm.nome}
                onChange={(e) => setEmpresaForm((f) => ({ ...f, nome: e.target.value }))}
                autoFocus
              />
              <Input
                label="Razão Social"
                placeholder="Farmácia Saúde Vital LTDA"
                value={empresaForm.razao_social}
                onChange={(e) => setEmpresaForm((f) => ({ ...f, razao_social: e.target.value }))}
              />
              <Input
                label="CNPJ"
                placeholder="00.000.000/0000-00"
                value={empresaForm.cnpj}
                onChange={(e) => setEmpresaForm((f) => ({ ...f, cnpj: maskCNPJ(e.target.value) }))}
                inputMode="numeric"
              />
            </div>
          </div>

          {error && <ErrorBox message={error} />}

          <div className="flex justify-between items-center mt-2 pt-4 border-t border-[#f1f5f9]">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-[#94a3b8] hover:text-[#64748b] transition-colors"
            >
              Pular (adicionar depois)
            </button>
            <Button type="submit" loading={loading} disabled={!empresaForm.nome.trim()}>
              Adicionar empresa
            </Button>
          </div>
        </form>
      )}
    </Modal>
  )
}
